/**
 * The board surface for a record-backed tracker: the toolbar, the views, and
 * the wiring between them and `useRecordBoard`.
 *
 * It is the counterpart to `CustomTrackerBoard`, and the differences all trace
 * back to one fact: the rows come from the server a page at a time, and the
 * board is never handed the whole set. So it offers Table and Kanban only
 * (Dashboard and Timeline chart the rows in hand), it drops the row and group
 * drag handles (the server decides the order), it takes its cap notice out (a
 * paged board has no cap to hit), and every number it prints is one the
 * response carried rather than one it counted.
 *
 * Comments need nothing here either: a thread is keyed by `(trackerId, rowKey)`
 * whichever board it is on. What differs is behind the endpoint, where an asset
 * row's thread is the asset's own rather than one the board keeps beside it, and
 * a row from another workspace has no thread this board can reach.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Alert, AlertAction, AlertDescription, AlertTitle, Button } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';

import type { Tracker, TrackerViewType } from '@/entities/tracker';
import { track } from '@/shared/analytics';
import { useAssetApprovalsHidden } from '@/shared/hooks/useAssetApprovalsHidden';
import { backToState } from '@/shared/lib/back-to';
import { EmptyState } from '@/shared/ui';

import type { CustomColumnType } from '../../lib/board-columns';
import { NO_VALUE } from '../../lib/record-query';
import {
  hasRecordFile,
  hasRecordRoute,
  recordRowFileId,
  recordRowHref,
  toBoardRowItems,
} from '../../lib/record-rows';
import { RecordUsersProvider } from '../../lib/record-users';
import { buildRecordSortChoices } from '../../lib/sort-choices';
import { boardTypeOf } from '../../lib/trackers-analytics';
import { useActiveView } from '../../lib/useActiveView';
import { useCommentDeeplink } from '../../lib/useCommentDeeplink';
import { useRecordBoard } from '../../lib/useRecordBoard';
import { useRecentFilterColumns } from '../../lib/usePersistentFilters';
import { BoardViewTabs } from '../BoardViewTabs';
import { ButtonConfigModal } from '../ButtonConfigModal';
import { FormulaConfigModal } from '../FormulaConfigModal';
import { KanbanView, type BoardCardData } from '../KanbanView';
import { RecordPreviewModal } from '../RecordPreviewModal';
import { StatusConfigModal } from '../StatusConfigModal';
import { TrackerCommentsPanel } from '../TrackerCommentsPanel';
import { TrackersBoardTable, type BoardGroup } from '../TrackersBoardTable';
import { TrackersToolbar } from '../TrackersToolbar';
import { WorkspaceScopeChip } from '../WorkspaceScopeChip';

/**
 * Both views a record board offers. Dashboard and Timeline are left out rather
 * than disabled: each one draws the items it is given, which here is whichever
 * pages have loaded, and a chart of the first fifty rows labelled as the whole
 * tracker is worse than no chart.
 */
const RECORD_VIEWS: readonly TrackerViewType[] = ['table', 'kanban'];

/** Brief-bound column types, which no record board can fill. */
const EXCLUDED_TYPES: readonly CustomColumnType[] = ['brief_type', 'assets', 'project'];

/** Nothing on this board reorders, so the drag callbacks have nothing to do. */
const noop = () => {};

export function RecordTrackerBoard({ tracker }: { tracker: Tracker }) {
  const { t } = useTranslation('trackers');

  const navigate = useNavigate();
  const location = useLocation();
  const board = useRecordBoard(tracker);
  const boardType = boardTypeOf(tracker.itemSource);
  const trackerId = tracker.id;

  const { layout } = board;
  const views = useMemo(
    () => layout.views.filter((view) => RECORD_VIEWS.includes(view)),
    [layout.views],
  );
  const { activeView, setActiveView } = useActiveView(views);
  const setViews = layout.setViews;
  const addView = useCallback(
    (view: TrackerViewType) => {
      if (views.includes(view)) return;
      setViews([...views, view]);
      setActiveView(view);
      // `table` is pinned on, so it never reaches here; the guard is what tells
      // the analytics catalogue that, which has no arm for it.
      if (view !== 'table') {
        track('tracker_view_added', {
          tracker_id: trackerId,
          board_type: boardType,
          view_type: view,
        });
      }
    },
    [views, setViews, setActiveView, trackerId, boardType],
  );
  const removeView = useCallback(
    (view: TrackerViewType) => {
      if (view === 'table') return;
      setViews(views.filter((current) => current !== view));
      track('tracker_view_removed', {
        tracker_id: trackerId,
        board_type: boardType,
        view_type: view,
      });
    },
    [views, setViews, trackerId, boardType],
  );

  const { openRowKey, focusCommentId, openComments, closeComments } = useCommentDeeplink();

  const [recentFilterColumns, noteFilterColumnUsed] = useRecentFilterColumns(trackerId);
  const sortChoices = useMemo(
    () => buildRecordSortChoices(board.fields, (field) => t(field.labelKey)),
    [board.fields, t],
  );
  const fieldTypeById = useMemo(
    () => new Map(board.fields.map((field) => [field.id, field.type])),
    [board.fields],
  );

  // One instant for every "is this late" decision on the page, for the reason
  // `useRecordBoard` holds one for the query: two `new Date()` calls can
  // straddle midnight and disagree about which day today is.
  const now = useMemo(() => new Date(), []);
  const untitledLabel = t('trackersBoard.recordTrackerBoard.untitled');
  const { boardWorkspaceId, workspaceLabel } = board;
  const rowOptions = useMemo(
    () => ({
      itemSource: tracker.itemSource,
      titleField: board.titleField,
      untitledLabel,
      now,
      boardWorkspaceId,
      workspaceLabel,
    }),
    [tracker.itemSource, board.titleField, untitledLabel, now, boardWorkspaceId, workspaceLabel],
  );

  const itemByKey = useMemo(
    () => new Map(toBoardRowItems(board.rows, rowOptions).map((item) => [item.id, item])),
    [board.rows, rowOptions],
  );

  const groups = useMemo<BoardGroup[]>(
    () =>
      board.groups.map((group) => ({
        id: group.id,
        title: group.title,
        color: group.color,
        items: group.rows.flatMap((row) => itemByKey.get(row.rowKey) ?? []),
        // Both counts are the server's for the current query. The table draws
        // on the difference to tell "this group is empty" from "your filters
        // emptied it", which a server-narrowed board cannot answer: it is never
        // told what the group holds unfiltered.
        itemCount: group.count,
        unfilteredItemCount: group.count,
      })),
    [board.groups, itemByKey],
  );
  const groupIds = useMemo(() => groups.map((group) => group.id), [groups]);

  // Collapsed rather than expanded, so a group that appears later — a new
  // status bucket, a regrouping — arrives open like the rest instead of
  // needing an effect to notice it.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<ReadonlySet<string>>(new Set());
  const expandedGroupIds = useMemo(
    () => new Set(groupIds.filter((id) => !collapsedGroupIds.has(id))),
    [groupIds, collapsedGroupIds],
  );
  const toggleGroup = useCallback((groupId: string) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (!next.delete(groupId)) next.add(groupId);
      return next;
    });
  }, []);

  const rowByKey = useMemo(() => new Map(board.rows.map((row) => [row.rowKey, row])), [board.rows]);
  // A record's own page sits under its project, so its Back button would drop
  // the person into a folder listing they never came from. Carrying the board's
  // own URL - filters, view and all - gives it somewhere truthful to return to.
  const returnHere = useMemo(
    () => backToState(`${location.pathname}${location.search}`),
    [location.pathname, location.search],
  );
  const openRecord = useCallback(
    (rowKey: string) => {
      const row = rowByKey.get(rowKey);
      const href = row ? recordRowHref(row, tracker.itemSource) : null;
      if (href) void navigate(href, { state: returnHere });
    },
    [rowByKey, tracker.itemSource, navigate, returnHere],
  );
  // Whether the board has a destination at all. Left undefined when it does
  // not, so no row offers a pointer and a hover state for navigation that
  // cannot happen; a row whose own record is missing an id its route needs is
  // caught a layer down, by `routable` on the row item. An asset's page sits
  // inside the approvals route tree, so with approvals hidden that route
  // bounces the person home: the board has the id to build the link and still
  // nowhere to send them.
  const approvalsHidden = useAssetApprovalsHidden();
  const sourceRoutable =
    hasRecordRoute(tracker.itemSource) && !(boardType === 'assets' && approvalsHidden);
  const onRowClick = sourceRoutable ? openRecord : undefined;

  // A record that IS a file shows one, and its thumbnail cell opens it here
  // rather than sending the person to the asset's page for a look. Offered off
  // the source, which is what decides whether the files endpoint can answer for
  // a row at all - a board that merely carries a file field has a picture to
  // show and no id to ask for it with.
  const [previewRowKey, setPreviewRowKey] = useState<string | null>(null);
  const openPreview = useCallback((rowKey: string) => setPreviewRowKey(rowKey), []);
  const onOpenPreview = hasRecordFile(tracker.itemSource) ? openPreview : undefined;
  const previewRow = previewRowKey !== null ? rowByKey.get(previewRowKey) : undefined;
  const closePreview = useCallback((open: boolean) => {
    if (!open) setPreviewRowKey(null);
  }, []);

  // Off the row items rather than off the rows, so a card and its table row
  // read the same cell for the same column.
  const cards = useMemo<BoardCardData[]>(
    () =>
      [...itemByKey.values()].map((item) => ({
        id: item.id,
        title: item.title,
        values: item.customColumnValues ?? undefined,
        routable: item.routable,
      })),
    [itemByKey],
  );

  // A Kanban lane is a status bucket, and the server already counts those for
  // the filter chips whether or not the board is grouped by status. What the
  // board is grouped by wins where that is a status, since a source can carry
  // more than one (an asset has both a review state and an approval state) and
  // the lanes should agree with the grouping the person chose.
  const statusColumns = board.customColumns.filter((column) => column.type === 'status');
  const statusColumnId = (
    statusColumns.find((column) => column.id === board.groupBy) ?? statusColumns.at(0)
  )?.id;
  const laneCount = useCallback(
    (statusId: string | null) => {
      if (!statusColumnId) return undefined;
      return board.filterValueCounts.get(statusColumnId)?.get(statusId ?? NO_VALUE);
    },
    [statusColumnId, board.filterValueCounts],
  );

  const { setCellValue, isEditable } = board;
  const setStatus = useCallback(
    (rowKey: string, statusId: string | null) => {
      if (statusColumnId) setCellValue(rowKey, statusColumnId, statusId);
    },
    [statusColumnId, setCellValue],
  );
  const isCardMovable = useCallback(
    (rowKey: string) => statusColumnId !== undefined && isEditable(statusColumnId, rowKey),
    [statusColumnId, isEditable],
  );

  // Configure only ever reaches a column the board owns: a record field is
  // deliberately absent from `layout.customColumns`, so the lookup misses and
  // no modal opens. See `useRecordBoard`.
  const [configuringColumnId, setConfiguringColumnId] = useState<string | null>(null);
  const configuringColumn = configuringColumnId
    ? layout.customColumns.find((column) => column.id === configuringColumnId)
    : undefined;
  const closeConfig = useCallback((open: boolean) => {
    if (!open) setConfiguringColumnId(null);
  }, []);

  // Refs for the shared horizontal-scroll sync in TrackersBoardTable.
  const { scrollContainersRef, syncedScrollLeftRef } = layout;
  const boardScrollRef = useRef<HTMLDivElement>(null);
  // State rather than a ref: the sentinel only exists on the branch that has
  // rows, so the observer has to be set up again each time that branch mounts,
  // which a ref would not tell the effect about.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);

  const { hasMore, isLoadingMore, loadMore } = board;
  useEffect(() => {
    if (!sentinel || !hasMore || isLoadingMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) loadMore();
      },
      // Fetch a screen early, so the next page is usually in before the user
      // reaches the rows it holds.
      { root: boardScrollRef.current, rootMargin: '400px' },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [sentinel, hasMore, isLoadingMore, loadMore]);

  const narrowed = board.searchInput.length > 0 || Object.keys(board.filters).length > 0;

  return (
    <RecordUsersProvider users={board.users}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-card">
        <TrackersToolbar
          leading={<WorkspaceScopeChip trackerId={trackerId} boardType={boardType} />}
          searchValue={board.searchInput}
          onSearchChange={board.setSearchInput}
          sortId={board.sortId}
          onSortChange={(next) => {
            board.setSortId(next);
            if (next) {
              track('tracker_sorted', {
                tracker_id: trackerId,
                board_type: boardType,
                source: 'toolbar',
                direction: next.endsWith('-desc') ? 'desc' : 'asc',
              });
            }
          }}
          sortOptions={sortChoices}
          groupByOptions={board.groupByOptions}
          groupByValue={board.groupBy}
          onGroupByChange={(next) => {
            board.setGroupBy(next);
            track('tracker_grouped', {
              tracker_id: trackerId,
              board_type: boardType,
              group_by: next ? (fieldTypeById.get(next) ?? 'none') : 'none',
            });
          }}
          filterColumns={board.filterColumns}
          filters={board.filters}
          onFiltersChange={(next) => {
            for (const key of Object.keys(next)) {
              if (key in board.filters) continue;
              const columnType = fieldTypeById.get(key);
              if (columnType) {
                track('tracker_filtered', {
                  tracker_id: trackerId,
                  board_type: boardType,
                  column_type: columnType,
                });
              }
            }
            board.setFilters(next);
          }}
          // No `rules` and no item counts: the narrowing happens on the server,
          // which has no `is not` and never reports what the board would have
          // held unfiltered. See `TrackersFilterPanel`.
          filterPanel={{
            trackerId,
            activeFilterCount: Object.keys(board.filters).length,
            counts: board.filterValueCounts,
            recentColumnIds: recentFilterColumns,
            onColumnUsed: noteFilterColumnUsed,
          }}
          trailing={
            <BoardViewTabs
              views={views}
              catalogue={RECORD_VIEWS}
              activeView={activeView}
              onSwitch={(view) => {
                setActiveView(view);
                track('tracker_view_switched', {
                  tracker_id: trackerId,
                  board_type: boardType,
                  view_type: view,
                });
              }}
              onAddView={addView}
              onRemoveView={removeView}
              onReorder={setViews}
            />
          }
        />

        <div
          ref={boardScrollRef}
          className={`flex-1 overflow-y-auto px-4 ${activeView === 'table' ? '' : 'py-4'}`}
        >
          {board.isError && board.rows.length === 0 ? (
            // Only when the board has nothing to show. A refetch that fails
            // over rows already on screen gets the banner below: this board
            // refetches on every window focus, so taking the rows away would
            // turn one flaky moment into an empty board.
            <EmptyState
              role="alert"
              className="min-h-40"
              title={t('trackersBoard.recordTrackerBoard.couldNotLoad')}
              description={t('trackersBoard.recordTrackerBoard.couldNotLoadDescription')}
            >
              <Button variant="outline" size="sm" onClick={board.refresh}>
                {t('trackersBoard.recordTrackerBoard.tryAgain')}
              </Button>
            </EmptyState>
          ) : board.isEmpty ? (
            <EmptyState
              className="min-h-40"
              title={t('trackersBoard.recordTrackerBoard.nothingHereYet')}
              description={t('trackersBoard.recordTrackerBoard.nothingHereYetDescription')}
            />
          ) : board.isFilteredEmpty ? (
            <EmptyState
              className="min-h-40"
              title={t('trackersBoard.recordTrackerBoard.noMatches')}
              description={t('trackersBoard.recordTrackerBoard.noMatchesDescription')}
            />
          ) : (
            <>
              {board.isError && (
                <Alert variant="destructive" role="alert" className="mb-3">
                  <AlertTitle>{t('trackersBoard.recordTrackerBoard.couldNotRefresh')}</AlertTitle>
                  <AlertDescription>
                    {t('trackersBoard.recordTrackerBoard.showingWhatLoadedLast')}
                  </AlertDescription>
                  <AlertAction>
                    <Button variant="outline" size="sm" onClick={board.refresh}>
                      {t('trackersBoard.recordTrackerBoard.tryAgain')}
                    </Button>
                  </AlertAction>
                </Alert>
              )}
              {activeView === 'kanban' ? (
                <KanbanView
                  items={cards}
                  customColumns={board.customColumns}
                  onItemClick={onRowClick}
                  onSetStatus={setStatus}
                  filtered={narrowed}
                  laneCount={laneCount}
                  statusColumnId={statusColumnId}
                  draggable={statusColumnId !== undefined && board.isEditable(statusColumnId)}
                  isItemDraggable={isCardMovable}
                />
              ) : (
                <TrackersBoardTable
                  groups={groups}
                  groupIds={groupIds}
                  isLoading={board.isLoading}
                  expandedGroupIds={expandedGroupIds}
                  onToggleGroup={toggleGroup}
                  onGroupDragStart={noop}
                  onGroupDragEnd={noop}
                  onReorderItems={noop}
                  reorderable={false}
                  columnOrder={layout.columnOrder}
                  columnWidths={layout.columnWidths}
                  onReorderColumns={layout.reorderColumns}
                  onSetColumnWidth={layout.setColumnWidth}
                  itemColumnWidth={layout.itemColumnWidth}
                  onSetItemColumnWidth={layout.setItemColumnWidth}
                  scrollContainersRef={scrollContainersRef}
                  syncedScrollLeftRef={syncedScrollLeftRef}
                  boardScrollRef={boardScrollRef}
                  tableMinContentWidth={layout.tableMinContentWidth}
                  excludeColumnTypes={EXCLUDED_TYPES}
                  onAddCustomColumn={layout.addCustomColumn}
                  onAddCustomColumnAfter={layout.addCustomColumnAfter}
                  onDuplicateColumn={layout.duplicateColumn}
                  onDeleteColumn={layout.deleteColumn}
                  onConfigureColumn={setConfiguringColumnId}
                  columnSort={board.columnSort}
                  onToggleColumnSort={board.toggleColumnSort}
                  columnDefs={layout.columnDefs}
                  customColumns={board.customColumns}
                  onRenameColumn={layout.renameColumn}
                  onItemClick={onRowClick}
                  onSetCellValue={board.setCellValue}
                  isColumnEditable={board.isEditable}
                  isColumnSortable={board.isSortable}
                  columnMenu={board.columnMenu}
                  onOpenPreview={onOpenPreview}
                  trackerId={trackerId}
                  showComments
                  onOpenComments={openComments}
                  openCommentsRowKey={openRowKey}
                />
              )}
              {/* Paging is scroll-driven, so the sentinel sits below whichever
                view is up rather than inside the table. */}
              <div ref={setSentinel} className="h-px" aria-hidden />
              {isLoadingMore ? (
                <p className="py-3 text-center text-xs text-muted-foreground">
                  {t('trackersBoard.recordTrackerBoard.loadingMore')}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>

      <StatusConfigModal
        open={configuringColumn?.type === 'status'}
        onOpenChange={closeConfig}
        options={configuringColumn?.statusOptions ?? []}
        onSave={(next) => {
          if (configuringColumnId) layout.updateStatusOptions(configuringColumnId, next);
        }}
      />
      <ButtonConfigModal
        open={configuringColumn?.type === 'button'}
        onOpenChange={closeConfig}
        caption={configuringColumn?.buttonCaption ?? configuringColumn?.label ?? ''}
        onSave={(next) => {
          if (configuringColumnId) layout.updateButtonCaption(configuringColumnId, next);
        }}
      />
      <FormulaConfigModal
        open={configuringColumn?.type === 'formula'}
        onOpenChange={closeConfig}
        expression={configuringColumn?.formulaExpression ?? ''}
        columns={board.customColumns.filter((column) => column.id !== configuringColumnId)}
        onSave={(next) => {
          if (configuringColumnId) layout.updateFormulaExpression(configuringColumnId, next);
        }}
      />
      <RecordPreviewModal
        open={previewRowKey !== null}
        onOpenChange={closePreview}
        platformProjectId={stringField(previewRow?.fields.platformProjectId)}
        fileId={previewRow ? recordRowFileId(previewRow, tracker.itemSource) : null}
        fileName={
          (previewRowKey !== null ? itemByKey.get(previewRowKey)?.title : undefined) ??
          untitledLabel
        }
        fileType={stringField(previewRow?.fields.fileType) ?? ''}
        href={sourceRoutable && previewRow ? recordRowHref(previewRow, tracker.itemSource) : null}
        backTo={returnHere}
      />
      <TrackerCommentsPanel
        open={openRowKey !== null && itemByKey.get(openRowKey)?.commentable !== false}
        trackerId={trackerId}
        boardType={boardType}
        rowKey={openRowKey}
        focusCommentId={focusCommentId}
        onClose={closeComments}
      />
    </RecordUsersProvider>
  );
}

/**
 * A record field the preview needs as a string. The rows endpoint types every
 * field as `unknown`, and a missing id has to read as absent rather than as the
 * string `"undefined"`.
 */
function stringField(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}
